import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Search,
  ShoppingCart,
  User,
  UserCircle2,
  X,
  KeyRound,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import type { MapleMarketApp, MapleMarketCategory } from '@/data/mapleMarketApps';
import MapleMarketLeaf from '@/components/maple-market/MapleMarketLeaf';
import MapleMarketNavGame from '@/components/maple-market/MapleMarketNavGame';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AddMapleKeyDialog from '@/components/maple-market/AddMapleKeyDialog';

export const MAPLE_MARKET_NAV_TOTAL_HEIGHT = '5.75rem';

interface MapleMarketNavbarProps {
  products: MapleMarketApp[];
  embedded?: boolean;
  isAdminAvailable?: boolean;
  onOpenAdmin?: () => void;
  onOpenLibrary?: () => void;
  onSelectCategory: (category: MapleMarketCategory | 'All') => void;
  onSelectApp: (appId: string) => void;
  showNavGame?: boolean;
  activeSection?: 'store' | 'library';
}

function getInitials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return 'U';
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export default function MapleMarketNavbar({
  products,
  embedded = false,
  isAdminAvailable = false,
  onOpenAdmin,
  onOpenLibrary,
  onSelectCategory,
  onSelectApp,
  showNavGame = false,
  activeSection = 'store',
}: MapleMarketNavbarProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  const filteredApps = useMemo(
    () =>
      searchQuery.length > 1
        ? products
            .filter(
              (app) =>
                app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                app.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
            )
            .slice(0, 5)
        : [],
    [products, searchQuery]
  );

  const navClassName = embedded
    ? 'sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur-xl'
    : 'fixed left-0 right-0 top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl';

  const accountName = profile?.name || user?.email?.split('@')[0] || 'Account';
  const accountEmail = user?.email || 'Not signed in';

  const handleCategorySelect = (category: MapleMarketCategory | 'All') => {
    setMobileMenuOpen(false);
    onSelectCategory(category);
  };

  const handleAppSelect = (appId: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    setMobileMenuOpen(false);
    onSelectApp(appId);
  };

  const handleOpenAdmin = () => {
    setMobileMenuOpen(false);
    onOpenAdmin?.();
  };

  const handleOpenProfile = () => {
    setMobileMenuOpen(false);
    navigate(user ? '/profile' : '/auth');
  };

  const handleOpenLibrary = () => {
    setMobileMenuOpen(false);
    if (onOpenLibrary) {
      onOpenLibrary();
      return;
    }
    navigate('/market/library');
  };

  const handleOpenCart = () => {
    if (!embedded) {
      navigate('/purchase');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <>
      <nav className={`${navClassName} relative overflow-hidden`}>
        <div className="container relative z-20 flex min-h-14 items-center justify-between gap-4 overflow-visible py-2">
          <button
            type="button"
            aria-label="Maple Market home"
            title="Maple Market"
            onClick={() => handleCategorySelect('All')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-primary"
          >
            {showNavGame ? <span className="sr-only">Maple Market home</span> : <MapleMarketLeaf />}
          </button>

          <div className="hidden items-center gap-6 text-sm font-medium md:flex">
            <button
              type="button"
              onClick={() => handleCategorySelect('All')}
              className={activeSection === 'store' ? 'text-foreground transition-colors hover:text-primary' : 'text-muted-foreground transition-colors hover:text-foreground'}
            >
              Store
            </button>
            <button
              type="button"
              onClick={handleOpenLibrary}
              className={activeSection === 'library' ? 'text-foreground transition-colors hover:text-primary' : 'text-muted-foreground transition-colors hover:text-foreground'}
            >
              Library
            </button>
            <button type="button" onClick={() => handleCategorySelect('Games')} className="text-muted-foreground transition-colors hover:text-foreground">
              Games
            </button>
            <button type="button" onClick={() => handleCategorySelect('Office')} className="text-muted-foreground transition-colors hover:text-foreground">
              Apps
            </button>
            <button type="button" onClick={() => handleCategorySelect('Creative')} className="text-muted-foreground transition-colors hover:text-foreground">
              Creative
            </button>
          </div>

          <div className="relative hidden max-w-md flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={activeSection === 'library' ? 'Search your library...' : 'Search apps and games...'}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              className="h-9 w-full rounded-lg bg-secondary/90 pl-9 pr-4 text-sm text-foreground outline-none ring-1 ring-transparent transition-all placeholder:text-muted-foreground focus:ring-primary/50"
            />
            {searchOpen && filteredApps.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 overflow-hidden rounded-lg border border-border bg-card shadow-card">
                {filteredApps.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onMouseDown={() => handleAppSelect(app.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent"
                  >
                    <img src={app.image} alt={app.name} className="h-10 w-14 rounded object-cover" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{app.name}</div>
                      <div className="text-xs text-muted-foreground">{app.category}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {embedded && isAdminAvailable && (
              <Button type="button" variant="outline" size="sm" className="hidden gap-2 lg:inline-flex xl:hidden" onClick={handleOpenAdmin}>
                <LayoutDashboard className="h-4 w-4" />
                Admin
              </Button>
            )}
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/90 hover:text-foreground sm:hidden"
            >
              <Search className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleOpenCart}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/90 hover:text-foreground"
            >
              <ShoppingCart className="h-4 w-4" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/90 hover:text-foreground"
                >
                  {user ? (
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={profile?.avatar} alt={accountName} />
                      <AvatarFallback className="text-[10px] font-semibold">{getInitials(accountName)}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  {embedded && isAdminAvailable && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {user ? (
                  <>
                    <DropdownMenuLabel className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={profile?.avatar} alt={accountName} />
                          <AvatarFallback>{getInitials(accountName)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-foreground">{accountName}</div>
                          <div className="truncate text-xs text-muted-foreground">{accountEmail}</div>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleOpenProfile} className="gap-2">
                      <UserCircle2 className="h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleOpenLibrary} className="gap-2">
                      <BookOpen className="h-4 w-4" />
                      Maple Market Library
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setAddKeyOpen(true)} className="gap-2">
                      <KeyRound className="h-4 w-4" />
                      Add Maple Key
                    </DropdownMenuItem>
                    {embedded && isAdminAvailable && (
                      <DropdownMenuItem onClick={handleOpenAdmin} className="gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        Marketplace Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleSignOut} className="gap-2 text-destructive focus:text-destructive">
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel className="text-sm text-muted-foreground">Sign in to buy apps and use your library.</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleOpenProfile} className="gap-2">
                      <LogIn className="h-4 w-4" />
                      Sign in
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/90 hover:text-foreground md:hidden"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {showNavGame && (
          <div className="relative z-10 h-9 border-t border-border/70 bg-background/70">
            <MapleMarketNavGame className="z-10" height={36} />
          </div>
        )}

        {mobileMenuOpen && (
          <div className="relative z-20 border-t border-border bg-card/95 px-4 py-4 backdrop-blur-xl md:hidden">
            <div className="space-y-2">
              <button type="button" onClick={() => handleCategorySelect('All')} className="block w-full rounded-lg px-3 py-2 text-left text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                Store
              </button>
              <button type="button" onClick={handleOpenLibrary} className="block w-full rounded-lg px-3 py-2 text-left text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                Library
              </button>
              <button type="button" onClick={() => handleCategorySelect('Games')} className="block w-full rounded-lg px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                Games
              </button>
              <button type="button" onClick={() => handleCategorySelect('Office')} className="block w-full rounded-lg px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                Apps
              </button>
              <button type="button" onClick={() => handleCategorySelect('Creative')} className="block w-full rounded-lg px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                Creative
              </button>
              {user && (
                <button type="button" onClick={() => setAddKeyOpen(true)} className="block w-full rounded-lg px-3 py-2 text-left text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                  Add Maple Key
                </button>
              )}
              {embedded && isAdminAvailable && (
                <button type="button" onClick={handleOpenAdmin} className="block w-full rounded-lg px-3 py-2 text-left text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                  Marketplace Admin
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
      <AddMapleKeyDialog open={addKeyOpen} onOpenChange={setAddKeyOpen} />
    </>
  );
}
